import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import * as authService from '../services/authService'

export const AuthContext = createContext(null)

const TOKEN_STORAGE_KEY = 'aps_access_token'
const REFRESH_TOKEN_KEY = 'aps_refresh_token'
const TOKEN_EXPIRY_KEY = 'aps_token_expiry'
const USER_DATA_KEY = 'aps_user_data'

/**
 * Three-Legged Authentication Context
 * Uses backend API for all authentication operations
 * All OAuth operations are handled securely on the backend
 */

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [token, setToken] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Check authentication status on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Check if we have a stored token from previous login
        const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY)
        const storedUserData = localStorage.getItem(USER_DATA_KEY)
        const tokenExpiry = localStorage.getItem(TOKEN_EXPIRY_KEY)

        if (storedToken && storedUserData) {
          // Check if token is still valid
          const expiryTime = parseInt(tokenExpiry, 10)
          const isExpired = expiryTime && Date.now() > expiryTime

          if (isExpired) {
            console.warn('[AuthContext] Token expired, clearing storage')
            localStorage.removeItem(TOKEN_STORAGE_KEY)
            localStorage.removeItem(USER_DATA_KEY)
            localStorage.removeItem(TOKEN_EXPIRY_KEY)
            localStorage.removeItem(REFRESH_TOKEN_KEY)
          } else {
            // Restore user data from localStorage
            try {
              const userData = JSON.parse(storedUserData)
              setToken(storedToken)
              setUser({
                authenticated: true,
                ...userData,
              })
            } catch (parseErr) {
              console.error('[AuthContext] Failed to parse user data:', parseErr)
            }
          }
        }
      } catch (err) {
        setError(err.message)
        console.error('[AuthContext] Init auth error:', err)
      } finally {
        setLoading(false)
      }
    }

    initAuth()
  }, [])

  const login = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      // Call backend to get OAuth authorization URL
      // Backend generates PKCE parameters and stores in session
      const { authUrl, requestId } = await authService.getAuthorizationUrl()

      // Store requestId in localStorage for use in callback
      // Backend needs this to retrieve the correct session state
      // Using localStorage instead of sessionStorage for better persistence during redirects
      if (requestId) {
        localStorage.setItem('oauth_request_id', requestId)
        console.log('[AuthContext] Stored requestId:', requestId)
      }

      // Redirect to Autodesk OAuth
      window.location.href = authUrl
    } catch (err) {
      setError(err.message)
      setLoading(false)
      throw err
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      // Call backend logout if user exists
      const userId = user?.id || user?.sub
      if (userId) {
        await authService.logout(userId)
      }
    } catch (err) {
      console.warn('Backend logout failed:', err.message)
    } finally {
      // Clear frontend tokens and user data regardless of backend response
      localStorage.removeItem(TOKEN_STORAGE_KEY)
      localStorage.removeItem(REFRESH_TOKEN_KEY)
      localStorage.removeItem(TOKEN_EXPIRY_KEY)
      localStorage.removeItem(USER_DATA_KEY)
      setToken(null)
      setUser(null)
      setError(null)

      // Redirect to login page
      window.location.href = '/login'
    }
  }, [user])

  const setAuthData = useCallback((userData, accessToken) => {
    // Called after successful OAuth callback to update AuthContext
    console.log('[AuthContext] Setting auth data:', { userData, accessTokenLength: accessToken?.length })
    setToken(accessToken)
    const userObj = {
      authenticated: true,
      type: 'user',
      loginMethod: 'three-legged',
      loginTime: new Date().toISOString(),
      ...userData,
    }
    console.log('[AuthContext] User object:', userObj)
    setUser(userObj)

    // Store user data in localStorage for persistence across page refreshes
    try {
      localStorage.setItem(USER_DATA_KEY, JSON.stringify(userObj))
      console.log('[AuthContext] User data saved to localStorage')
    } catch (err) {
      console.error('[AuthContext] Failed to save user data:', err)
    }
  }, [])

  const refreshToken = useCallback(async () => {
    try {
      setLoading(true)

      const userId = user?.id || user?.sub
      const refreshTokenStr = localStorage.getItem(REFRESH_TOKEN_KEY)

      if (!userId || !refreshTokenStr) {
        throw new Error('Missing user ID or refresh token')
      }

      // Call backend to refresh token
      const result = await authService.refreshAccessToken(refreshTokenStr, userId)

      if (result.tokens && result.tokens.accessToken) {
        // Store new tokens
        localStorage.setItem(TOKEN_STORAGE_KEY, result.tokens.accessToken)
        localStorage.setItem(TOKEN_EXPIRY_KEY, Date.now() + result.tokens.expiresIn * 1000)
        if (result.tokens.refreshToken) {
          localStorage.setItem(REFRESH_TOKEN_KEY, result.tokens.refreshToken)
        }

        setToken(result.tokens.accessToken)
        return result.tokens.accessToken
      } else {
        throw new Error('No access token in refresh response')
      }
    } catch (err) {
      setError(err.message)
      await logout()
      throw err
    } finally {
      setLoading(false)
    }
  }, [user, logout])

  const value = {
    user,
    token,
    loading,
    error,
    login,
    logout,
    refreshToken,
    setAuthData,
    isAuthenticated: !!token,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
